import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AreaChart, Area, ResponsiveContainer, BarChart, Bar, Tooltip, XAxis } from 'recharts';
import * as adminService from '../../services/adminService';
import './AdminDashboardPage.css';

interface KPIs {
  mrr: number;
  arr: number;
  activeSubscribers: number;
  newSubsToday: number;
  cancellationsToday: number;
  trialConversionRate: number;
  churnRate: number;
  dau: number;
  totalUsers: number;
  userGrowthPercent: number;
}

interface SparklinePoint {
  date: string;
  count?: number;
  total?: number;
}

interface Sparklines {
  newUsers: SparklinePoint[];
  revenue: SparklinePoint[];
  dau: SparklinePoint[];
}

interface KPICardConfig {
  key: string;
  label: string;
  value: string;
  change?: number;
  sparkData?: SparklinePoint[];
  sparkKey?: string;
  color: string;
}

const AdminDashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation('admin');
  const tEn = i18n.getFixedT('en', 'admin');
  const isEn = i18n.language === 'en';

  /** Returns "本地名稱 (English Name)" for non-EN locales, or just the English name */
  const bl = (key: string): string => {
    const native = t(key);
    if (isEn) return native;
    const en = tEn(key);
    return `${native} (${en})`;
  };

  const [kpis, setKpis] = useState<KPIs | null>(null);
  const [sparklines, setSparklines] = useState<Sparklines | null>(null);
  const [activity, setActivity] = useState<adminService.AdminActivitySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [kpiData, sparkData, activityData] = await Promise.all([
        adminService.getDashboardKPIs(),
        adminService.getDashboardSparklines(),
        adminService.getAdminActivitySummary().catch(() => null),
      ]);
      setKpis(kpiData);
      setSparklines(sparkData);
      setActivity(activityData);
    } catch {
      setError(t('dashboard.fetchError', 'Failed to load dashboard data'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (val: number): string => {
    if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `$${(val / 1000).toFixed(1)}K`;
    return `$${val.toFixed(0)}`;
  };

  const formatNumber = (val: number): string => {
    if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
    return val.toLocaleString();
  };

  const formatPercent = (val: number): string => `${val.toFixed(1)}%`;

  const buildCards = (): KPICardConfig[] => {
    if (!kpis) return [];
    return [
      {
        key: 'mrr',
        label: bl('dashboard.mrr'),
        value: formatCurrency(kpis.mrr),
        color: '#667eea',
        sparkData: sparklines?.revenue,
        sparkKey: 'total',
      },
      {
        key: 'arr',
        label: bl('dashboard.arr'),
        value: formatCurrency(kpis.arr),
        color: '#764ba2',
      },
      {
        key: 'activeSubs',
        label: bl('dashboard.activeSubscribers'),
        value: formatNumber(kpis.activeSubscribers),
        color: '#16a34a',
      },
      {
        key: 'newSubs',
        label: bl('dashboard.newSubsToday'),
        value: formatNumber(kpis.newSubsToday),
        color: '#0ea5e9',
        sparkData: sparklines?.newUsers,
        sparkKey: 'count',
      },
      {
        key: 'cancellations',
        label: bl('dashboard.cancellationsToday'),
        value: formatNumber(kpis.cancellationsToday),
        color: '#dc2626',
      },
      {
        key: 'trialConversion',
        label: bl('dashboard.trialConversion'),
        value: formatPercent(kpis.trialConversionRate),
        change: kpis.trialConversionRate,
        color: '#ca8a04',
      },
      {
        key: 'churn',
        label: bl('dashboard.churnRate'),
        value: formatPercent(kpis.churnRate),
        color: '#dc2626',
      },
      {
        key: 'dau',
        label: bl('dashboard.dau'),
        value: formatNumber(kpis.dau),
        color: '#667eea',
        sparkData: sparklines?.dau,
        sparkKey: 'count',
      },
    ];
  };

  const renderSkeleton = () => (
    <div className="admin-dash-grid">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="admin-dash-card admin-dash-card--skeleton">
          <div className="admin-dash-skeleton-label" />
          <div className="admin-dash-skeleton-value" />
          <div className="admin-dash-skeleton-chart" />
        </div>
      ))}
    </div>
  );

  if (error) {
    return (
      <div className="admin-dash-page">
        <h1 className="admin-dash-title">{t('dashboard.title', 'Dashboard')}</h1>
        <div className="admin-dash-error">
          <div className="admin-dash-error-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <p className="admin-dash-error-text">{error}</p>
          <button className="admin-dash-retry-btn" onClick={fetchData}>
            {t('dashboard.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dash-page">
      <h1 className="admin-dash-title">{t('dashboard.title', 'Dashboard')}</h1>

      {loading ? (
        renderSkeleton()
      ) : kpis ? (
        <>
          <div className="admin-dash-grid">
            {buildCards().map((card) => (
              <div key={card.key} className="admin-dash-card">
                <div className="admin-dash-card-header">
                  <span className="admin-dash-card-label">{card.label}</span>
                </div>
                <div className="admin-dash-card-value" style={{ color: card.color }}>
                  {card.value}
                </div>
                {card.sparkData && card.sparkData.length > 0 && (
                  <div className="admin-dash-sparkline">
                    <ResponsiveContainer width="100%" height={40}>
                      <AreaChart data={card.sparkData}>
                        <defs>
                          <linearGradient id={`grad-${card.key}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={card.color} stopOpacity={0.3} />
                            <stop offset="100%" stopColor={card.color} stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <Area
                          type="monotone"
                          dataKey={card.sparkKey || 'count'}
                          stroke={card.color}
                          strokeWidth={1.5}
                          fill={`url(#grad-${card.key})`}
                          dot={false}
                          isAnimationActive={false}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Admin activity analytics */}
          {activity && (
            <section className="admin-dash-activity" aria-label={bl('dashboard.adminActivity')}>
              <div className="admin-dash-activity-head">
                <h2 className="admin-dash-activity-title">
                  {bl('dashboard.adminActivity')}
                </h2>
                <Link to="/admin/audit-log" className="admin-dash-activity-link">
                  {t('dashboard.viewAuditLog', 'View audit log')}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </Link>
              </div>

              <div className="admin-dash-activity-counts">
                <div className="admin-dash-activity-count">
                  <span className="admin-dash-activity-count-value">
                    {formatNumber(activity.counts.last24h)}
                  </span>
                  <span className="admin-dash-activity-count-label">
                    {t('dashboard.actionsLast24h', 'Actions (24h)')}
                  </span>
                </div>
                <div className="admin-dash-activity-count">
                  <span className="admin-dash-activity-count-value">
                    {formatNumber(activity.counts.last7d)}
                  </span>
                  <span className="admin-dash-activity-count-label">
                    {t('dashboard.actionsLast7d', 'Actions (7d)')}
                  </span>
                </div>
                <div className="admin-dash-activity-count">
                  <span className="admin-dash-activity-count-value">
                    {formatNumber(activity.counts.last30d)}
                  </span>
                  <span className="admin-dash-activity-count-label">
                    {t('dashboard.actionsLast30d', 'Actions (30d)')}
                  </span>
                </div>
              </div>

              <div className="admin-dash-activity-grid">
                <div className="admin-dash-activity-card">
                  <h3 className="admin-dash-activity-card-title">
                    {t('dashboard.activityTrend', 'Daily actions (14 days)')}
                  </h3>
                  {activity.daily.length > 0 ? (
                    <ResponsiveContainer width="100%" height={140}>
                      <BarChart data={activity.daily} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                        <XAxis
                          dataKey="day"
                          tickFormatter={(v: string) => v.slice(5)}
                          tick={{ fontSize: 11, fill: '#64748b' }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          cursor={{ fill: 'rgba(102,126,234,0.08)' }}
                          contentStyle={{ fontSize: 12, borderRadius: 8 }}
                        />
                        <Bar dataKey="count" fill="#667eea" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="admin-dash-activity-empty">
                      {t('dashboard.activityEmpty', 'No admin actions yet.')}
                    </p>
                  )}
                </div>

                <div className="admin-dash-activity-card">
                  <h3 className="admin-dash-activity-card-title">
                    {t('dashboard.topAdmins', 'Top admins (30d)')}
                  </h3>
                  {activity.topAdmins.length > 0 ? (
                    <ul className="admin-dash-activity-list">
                      {activity.topAdmins.map((a) => (
                        <li key={a.adminId} className="admin-dash-activity-row">
                          <Link to={`/admin/users/${a.adminId}`} className="admin-dash-activity-name">
                            {a.username ?? a.email ?? a.adminId.slice(0, 8)}
                          </Link>
                          <span className="admin-dash-activity-count-pill">{a.count.toLocaleString()}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="admin-dash-activity-empty">
                      {t('dashboard.activityEmpty', 'No admin actions yet.')}
                    </p>
                  )}
                </div>

                <div className="admin-dash-activity-card">
                  <h3 className="admin-dash-activity-card-title">
                    {t('dashboard.topActions', 'Top action types (30d)')}
                  </h3>
                  {activity.topActions.length > 0 ? (
                    <ul className="admin-dash-activity-list">
                      {activity.topActions.map((a) => (
                        <li key={a.action} className="admin-dash-activity-row">
                          <Link
                            to={`/admin/audit-log?action=${encodeURIComponent(a.action)}`}
                            className="admin-dash-activity-name admin-dash-activity-name--mono"
                          >
                            {a.action}
                          </Link>
                          <span className="admin-dash-activity-count-pill">{a.count.toLocaleString()}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="admin-dash-activity-empty">
                      {t('dashboard.activityEmpty', 'No admin actions yet.')}
                    </p>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Summary row */}
          <div className="admin-dash-summary">
            <div className="admin-dash-summary-item">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <div className="admin-dash-summary-data">
                <span className="admin-dash-summary-value">
                  {formatNumber(kpis.totalUsers)}
                </span>
                <span className="admin-dash-summary-label">
                  {bl('dashboard.totalUsers')}
                </span>
              </div>
            </div>
            <div className="admin-dash-summary-item">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={kpis.userGrowthPercent >= 0 ? '#16a34a' : '#dc2626'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {kpis.userGrowthPercent >= 0 ? (
                  <>
                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                    <polyline points="17 6 23 6 23 12" />
                  </>
                ) : (
                  <>
                    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
                    <polyline points="17 18 23 18 23 12" />
                  </>
                )}
              </svg>
              <div className="admin-dash-summary-data">
                <span
                  className="admin-dash-summary-value"
                  style={{ color: kpis.userGrowthPercent >= 0 ? '#16a34a' : '#dc2626' }}
                >
                  {kpis.userGrowthPercent >= 0 ? '+' : ''}
                  {kpis.userGrowthPercent.toFixed(1)}%
                </span>
                <span className="admin-dash-summary-label">
                  {bl('dashboard.userGrowth')}
                </span>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default AdminDashboardPage;
